import React, { useEffect, useState } from 'react';
import { Grid, Typography, Box } from '@mui/material';
import ButtonGeneral from '../general/buttons/buttonGeneral';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { DataGrid, esES } from '@mui/x-data-grid';
import { useAuthContext } from '../../../contexts/authContext';

type Props = {
    projectKey?: string;
    title?: string;
    titleButtonAdd?: string;
    startIconButtonAdd?: JSX.Element | string;
    onClickAdd?: any;
    onClickBack?: () => void | null;
    columns?: any;
    rows?: any;
    rowCount?: number;
    loading?: boolean;
    checkboxSelection?: boolean | false;
    onSetPage?: any;
    disableRow?: boolean | false;
    disableAdd?: boolean | false;
    disabledAdd?: boolean | false;
    disableBack?: boolean | false;
    enabledSearch?: boolean | false;
    searchField?: JSX.Element | string;
    disableDataGrid?: false | boolean;
    titleButtonGeneral?: string;
    startIconButtonGeneral?: JSX.Element | string;
    onClickButtonGeneral?: any;
    enabledButtonGeneral?: boolean | false;
    enabledSecondButtonGeneral?: boolean | false;
    titleSecondButtonGeneral?: string;
    startIconSecondButtonGeneral?: JSX.Element | string;
    onClickSecondButtonGeneral?: any;
    sxProps?: boolean | false;
    message?: JSX.Element | string;
    isRowSelectable?: any;
    onRowSelection?: (values: string[]) => void;
    rowSelection?: any;
    filterAvanced?: false | boolean;
    onFilterAvanced?: any;
    showList?: boolean;
};

const ListView = ({
    projectKey,
    title,
    titleButtonAdd,
    startIconButtonAdd,
    onClickAdd,
    onClickBack,
    columns,
    rows,
    rowCount,
    checkboxSelection,
    disableRow,
    disableAdd,
    disabledAdd,
    disableBack,
    disableDataGrid,
    onSetPage,
    loading,
    enabledSearch,
    searchField,
    titleButtonGeneral,
    startIconButtonGeneral,
    onClickButtonGeneral,
    enabledButtonGeneral,
    enabledSecondButtonGeneral,
    titleSecondButtonGeneral,
    startIconSecondButtonGeneral,
    onClickSecondButtonGeneral,
    message,
    sxProps,
    isRowSelectable,
    onRowSelection,
    rowSelection,
    filterAvanced,
    onFilterAvanced,
    showList = true,
}: Props) => {
    const { modeTheme } = useAuthContext();

    const [paginationModel, setPaginationModel] = useState({
        pageSize: 20,
        page: 0,
    });

    useEffect(() => {
        setPaginationModel((prev) => ({
            ...prev,
            page: 0,
        }));
    }, [projectKey]);

    const [rowsIds, setRowsIds] = useState<string[]>([]);

    useEffect(() => {
        if (!disableDataGrid && onSetPage) {
            onSetPage(paginationModel);
        }
        if (checkboxSelection) {
            if (rowsIds.length === 0 && rowSelection.length > 0) {
                setRowsIds(rowSelection);
            }
            onRowSelection!(rowsIds);
        }
    }, [
        disableDataGrid,
        onSetPage,
        paginationModel,
        rowSelection,
        checkboxSelection,
        rowsIds,
        rows,
    ]);

    const validateFilter = (newModel: any) => {
        if (newModel) {
            const data = newModel.items[0] || [];
            if (data?.value) {
                onFilterAvanced(data);
            } else {
                onFilterAvanced(null);
            }
        }
    };

    return (
        <Grid mx={5}>
            {title && (
                <>
                    <Typography
                        color="primary"
                        component="h3"
                        sx={{
                            flexGrow: 1,
                            fontSize: { lg: 24, md: 22, xs: 20 },
                            color:
                                modeTheme === 'darkMode' ? '#000' : undefined,
                        }}
                    >
                        {title}
                    </Typography>
                    <hr color="#9F9E9EFF" />
                </>
            )}
            <Grid container sx={{ minWidth: 300, mt: 2 }}>
                {enabledSearch && (
                    <Grid item xs={12} sm={6.5} md={6.5} lg={6.5}>
                        {searchField}
                    </Grid>
                )}
                <Grid
                    item
                    xs={12}
                    sm={enabledSearch ? 5.5 : 12}
                    md={enabledSearch ? 5.5 : 12}
                    lg={enabledSearch ? 5.5 : 12}
                    sx={{
                        mt: {
                            xs: enabledSearch ? 2 : 0,
                            sm: 0,
                        },
                        textAlign: 'right',
                    }}
                >
                    {enabledSecondButtonGeneral === true && (
                        <ButtonGeneral
                            onClick={onClickSecondButtonGeneral}
                            startIcon={startIconSecondButtonGeneral || ''}
                            title={titleSecondButtonGeneral || ''}
                            sx={{
                                mr: {
                                    xs: enabledSecondButtonGeneral ? 1 : 0,
                                    sm: 1,
                                },
                                mt: { xs: enabledButtonGeneral ? 1 : 0, sm: 1 },
                            }}
                        />
                    )}
                    {enabledButtonGeneral === true && (
                        <ButtonGeneral
                            onClick={onClickButtonGeneral}
                            startIcon={startIconButtonGeneral || ''}
                            title={titleButtonGeneral || ''}
                            sx={{
                                mr: {
                                    xs: enabledSecondButtonGeneral ? 0 : 1,
                                    sm: 1,
                                },
                                mt: { xs: enabledButtonGeneral ? 1 : 0, sm: 1 },
                            }}
                        />
                    )}
                    {disableAdd !== true && (
                        <ButtonGeneral
                            onClick={onClickAdd}
                            startIcon={startIconButtonAdd || ''}
                            title={titleButtonAdd || ''}
                            sx={{
                                mr: { xs: 1, sm: 1 },
                                mt: { xs: enabledButtonGeneral ? 1 : 0, sm: 1 },
                            }}
                            disabled={disabledAdd}
                        />
                    )}
                    {disableBack !== true && (
                        <ButtonGeneral
                            onClick={onClickBack}
                            startIcon={<ArrowBackIcon />}
                            title="Volver"
                            sx={{
                                mr: { xs: 0, sm: 1 },
                                mt: { xs: enabledButtonGeneral && 1, sm: 1 },
                            }}
                        />
                    )}
                </Grid>
            </Grid>
            {message && message}
            {!disableDataGrid && (
                <Box
                    mt={2.5}
                    sx={{ minWidth: 300 }}
                    display={showList ? 'block' : 'none'}
                >
                    <DataGrid
                        localeText={
                            esES.components.MuiDataGrid.defaultProps.localeText
                        }
                        rows={rows || []}
                        columns={columns}
                        autoHeight
                        paginationModel={paginationModel}
                        rowCount={rowCount}
                        paginationMode="server"
                        loading={loading}
                        pageSizeOptions={[20, 50, 100]}
                        onFilterModelChange={(newModel) =>
                            filterAvanced && validateFilter(newModel)
                        }
                        onPaginationModelChange={setPaginationModel}
                        checkboxSelection={checkboxSelection}
                        disableRowSelectionOnClick={disableRow}
                        isRowSelectable={isRowSelectable}
                        rowSelectionModel={checkboxSelection ? rowsIds : []}
                        onRowSelectionModelChange={(rows: any) => {
                            setRowsIds(rows);
                        }}
                        sx={
                            sxProps
                                ? {}
                                : {
                                    '.MuiTablePagination-displayedRows': {
                                        marginTop: '1rem',
                                    },
                                    '.MuiTablePagination-selectLabel': {
                                        marginTop: '1rem',
                                    },
                                    '.MuiDataGrid-columnHeaders': {
                                        color: 'primary.contrastText',
                                        backgroundColor: 'primary.main',
                                    },
                                    '.MuiDataGrid-sortIcon': {
                                        color: 'primary.contrastText',
                                    },
                                    '.MuiSvgIcon-fontSizeSmall': {
                                        color: 'primary.contrastText',
                                    },
                                    '.MuiDataGrid-iconSeparator': {
                                        color: 'primary.contrastText',
                                    },
                                    '.MuiDataGrid-footerContainer': {
                                        display: !onSetPage ? 'none' : 'flex',
                                    },
                                }
                        }
                    />
                </Box>
            )}
        </Grid>
    );
};

export default ListView;
